import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

export type SubCrumb = {
  /** Shown as the trailing, current crumb. */
  label: string
  /** Returns the page to the state where this crumb no longer applies. */
  onClear: () => void
}

type SubCrumbContextValue = {
  subCrumb: SubCrumb | null
  setSubCrumb: (crumb: SubCrumb | null) => void
}

const SubCrumbContext = createContext<SubCrumbContextValue>({
  subCrumb: null,
  setSubCrumb: () => undefined,
})

/**
 * Lets a page contribute one crumb that the router cannot know about — a view
 * that is component state rather than a route, so `useMatches` has nothing to
 * report. Same contract as the Planner Portal's, where the work-centre
 * dashboard's four views use it; the Scheduler's trailing `selectedSection`
 * crumb works the same way.
 */
export const SubCrumbProvider = ({ children }: { children: ReactNode }) => {
  const [subCrumb, setSubCrumb] = useState<SubCrumb | null>(null)
  const value = useMemo(() => ({ subCrumb, setSubCrumb }), [subCrumb])
  return (
    <SubCrumbContext.Provider value={value}>
      {children}
    </SubCrumbContext.Provider>
  )
}

/** Read by the breadcrumb trail. */
export const useSubCrumb = () => useContext(SubCrumbContext)

/**
 * Publish a trailing crumb for as long as `label` is set, and clear it on
 * unmount. `onClear` is held in a ref so a page can pass an inline closure
 * without re-publishing on every render.
 */
export const usePublishSubCrumb = (
  label: string | null | undefined,
  onClear: () => void
) => {
  const { setSubCrumb } = useContext(SubCrumbContext)
  const clearRef = useRef(onClear)
  clearRef.current = onClear

  const clear = useCallback(() => clearRef.current(), [])

  useEffect(() => {
    setSubCrumb(label ? { label, onClear: clear } : null)
    return () => setSubCrumb(null)
  }, [label, clear, setSubCrumb])
}
